// Cliente HTTP: access token só em memória (ADR-004); refresh via cookie httpOnly.

/** Resposta sem o JSON da API: veio do proxy (Vite/Caddy) — a API caiu, está reiniciando ou demorou demais. */
export function erroSemCorpo(status: number): string {
  if (status === 504) return 'O servidor demorou demais para responder. Tente filtrar mais e busque de novo.';
  if (status === 502 || status === 503 || status === 500) return 'O servidor está indisponível no momento (reiniciando ou fora do ar). Tente de novo em alguns segundos.';
  if (status === 413) return 'Os dados enviados são grandes demais.';
  return `Algo deu errado (HTTP ${status}). Tente de novo.`;
}

let accessToken: string | null = null;
let aoPerderSessao: (() => void) | null = null;

export const setAccessToken = (t: string | null) => {
  accessToken = t;
};
export const onSessaoPerdida = (fn: () => void) => {
  aoPerderSessao = fn;
};
let aoBloquear: (() => void) | null = null;
/** Empresa bloqueada no meio da sessão (teste grátis venceu, suspensão): recarrega o /me e mostra a tela de bloqueio. */
export const onEmpresaBloqueada = (fn: () => void) => {
  aoBloquear = fn;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public meta?: Record<string, unknown>,
    public errors?: Array<{ path: string; message: string }>,
  ) {
    super(message);
  }
}

let refreshing: Promise<boolean> | null = null;

/** Single-flight: várias requisições com 401 disparam um único refresh. */
export function refresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) {
          accessToken = null;
          return false;
        }
        accessToken = (await r.json()).accessToken;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

export async function api<T = unknown>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const fazer = () =>
    fetch(`/api/v1${path}`, {
      method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
      credentials: 'same-origin',
      headers: {
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });

  let r = await fazer();
  if (r.status === 401 && !path.startsWith('/auth/')) {
    if (await refresh()) r = await fazer();
    else aoPerderSessao?.();
  }
  if (r.status === 204) return undefined as T;
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    if (r.status === 403 && (json.code === 'TESTE_EXPIRADO' || json.code === 'TENANT_BLOQUEADO' || json.code === 'TROCAR_SENHA')) aoBloquear?.();
    throw new ApiError(r.status, json.code ?? 'ERRO', json.detail ?? erroSemCorpo(r.status), json.meta, json.errors);
  }
  return json as T;
}

/** GET de binário (ex.: imagem da campanha) com o mesmo tratamento de sessão. */
export async function apiBlob(path: string): Promise<Blob> {
  const fazer = () =>
    fetch(`/api/v1${path}`, {
      credentials: 'same-origin',
      headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
    });
  let r = await fazer();
  if (r.status === 401) {
    if (await refresh()) r = await fazer();
    else aoPerderSessao?.();
  }
  if (!r.ok) {
    const json = await r.json().catch(() => ({}));
    throw new ApiError(r.status, json.code ?? 'ERRO', json.detail ?? erroSemCorpo(r.status));
  }
  return r.blob();
}

export function mensagemErro(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.errors?.length) return e.errors.map((x) => x.message).join(' · ');
    return e.message;
  }
  return 'Não foi possível concluir. Verifique sua conexão.';
}
